/**
 * Phase 105-Agent-A — TaskPinLayer.
 *
 * Renders one pin per task on the Leaflet map. Pin color codes from the
 * status field:
 *   • new / pending          → red (#ef4444)
 *   • assigned/pickup_assigned → blue (#3b82f6)   ← pending acceptance
 *   • accepted (legacy)      → amber (#f59e0b)
 *   • picked / in_transit    → green (#10b981)
 *   • out_for_delivery       → orange (#FF6B00)
 *   • delivered/cancelled    → gray (#94a3b8)
 *
 * Pickup coords are read from `order.pickup_location.lat/lng` (legacy
 * schema) with fallback to top-level `pickup_lat/lng`. Tapping a pin
 * surfaces the order via `onSelect(order)`.
 *
 * Phase 105-Agent-B polish: each pin now exposes a tiny Leaflet popup
 * with a "📍 Pickup in Maps" deep-link button (via the shared
 * `gmapsForTask` helper). Tapping the body opens the side sheet.
 */
import { Marker, Popup, createDotIcon } from "../tracking/mapProvider";
import { gmapsForTask } from "../../utils/mapsDeepLink";

function pickupLatLng(o) {
  const lat = o?.pickup_location?.lat ?? o?.pickup_lat;
  const lng = o?.pickup_location?.lng ?? o?.pickup_lng;
  if (lat == null || lng == null) return null;
  const nlat = Number(lat); const nlng = Number(lng);
  if (!Number.isFinite(nlat) || !Number.isFinite(nlng)) return null;
  return [nlat, nlng];
}

export function pinColorForStatus(status) {
  const s = String(status || "").toLowerCase();
  if (["delivered", "cancelled", "failed", "refunded"].includes(s))  return "#94a3b8";
  if (s === "out_for_delivery")                                       return "#FF6B00";
  if (["picked_up", "picked", "in_transit", "linehaul", "at_origin_hub", "at_dest_hub", "at_hub"].includes(s)) return "#10b981";
  if (["assigned", "pickup_assigned"].includes(s))                    return "#3b82f6";
  if (s === "accepted")                                                return "#f59e0b";
  return "#ef4444"; // new / pending / created / confirmed
}

export default function TaskPinLayer({ tasks, onSelect, selectedTrackingId }) {
  return (
    <>
      {(tasks || []).map((t) => {
        const ll = pickupLatLng(t);
        if (!ll) return null;
        const color = pinColorForStatus(t.status);
        const isSelected = t.tracking_id === selectedTrackingId;
        const icon = createDotIcon(color, isSelected ? 26 : 18);
        const pickupGmaps = gmapsForTask(t, "pickup");
        return (
          <Marker
            key={t.tracking_id}
            position={ll}
            icon={icon}
            eventHandlers={{
              click: () => { if (onSelect) onSelect(t); },
            }}
          >
            <Popup>
              <div className="text-xs font-sans" data-testid={`pin-popup-${t.tracking_id}`}>
                <div className="font-mono font-semibold text-slate-900">{t.tracking_id}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 capitalize">
                  {String(t.status || "").replace(/_/g, " ")}
                </div>
                {t.pickup_address && (
                  <div className="text-[11px] text-slate-700 mt-1.5 leading-snug max-w-[200px] line-clamp-2">
                    {t.pickup_address}
                  </div>
                )}
                <div className="flex flex-col gap-1.5 mt-2">
                  <button
                    type="button"
                    onClick={() => { if (onSelect) onSelect(t); }}
                    className="text-[11px] font-semibold px-2 py-1 rounded-md bg-orange-50 text-[#FF6B00] hover:bg-orange-100 border border-orange-200"
                    data-testid={`pin-popup-open-${t.tracking_id}`}
                  >
                    Open task details →
                  </button>
                  {pickupGmaps && (
                    <a
                      href={pickupGmaps}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] font-semibold px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 no-underline text-center"
                      data-testid={`pin-popup-pickup-maps-${t.tracking_id}`}
                    >
                      📍 Pickup in Maps
                    </a>
                  )}
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}
